import {
  keepPreviousData,
  type UseQueryOptions,
  type UseQueryResult,
  useQuery,
} from "@tanstack/react-query";

/// Workspace-gated list-query seam.
///
/// The webui has ~15 list hooks that share the same shape: a query
/// keyed by `[resource, workspaceId, query]`, a `keepPreviousData`
/// placeholder (Q4, search inputs must keep focus), a 30s default
/// stale, an `enabled` gate on the workspaceId, and a throw inside
/// the queryFn if the workspaceId is undefined. This hook owns that
/// policy so each feature hook is a 5-line thunk that names its
/// resource.
///
/// The caller passes the full `queryKey` so existing cache shapes
/// (which mutations may invalidate by prefix) stay untouched.

interface Args<T> {
  /** Full queryKey tuple, caller owns the shape so cache lookups by
   *  prefix (used by mutation invalidations) keep working. */
  queryKey: readonly unknown[];
  workspaceId: string | undefined;
  /** Called with the non-null workspaceId once the `enabled` gate passes. */
  fetch: (workspaceId: string) => Promise<T>;
  /** Default 30_000. Override for slower-changing resources. */
  staleMs?: number;
  /** AND-ed with the workspaceId presence check. Use when the query
   *  should only fire under additional conditions (e.g. an admin tab
   *  being active). Defaults to true. */
  extraEnabled?: boolean;
  /** Optional polling interval. May be a static number (ms), `false` to
   *  disable, or a function that inspects the latest data and returns ms
   *  or false to stop. Passed straight to TanStack Query. */
  refetchInterval?: UseQueryOptions<T>["refetchInterval"];
}

export function useListResource<T>(args: Args<T>): UseQueryResult<T> {
  return useQuery<T>({
    queryKey: args.queryKey,
    queryFn: () => {
      if (!args.workspaceId) throw new Error("workspaceId required");
      return args.fetch(args.workspaceId);
    },
    enabled: Boolean(args.workspaceId) && (args.extraEnabled ?? true),
    staleTime: args.staleMs ?? 30_000,
    placeholderData: keepPreviousData,
    refetchInterval: args.refetchInterval,
  });
}
